import React, { useEffect, useState, useMemo } from 'react';
import { Map, MarkerVariant } from '@otp/ui';
import clsx from 'clsx';

import { StoreModel } from 'src/graphql/generated/graphql';
import { RestaurantsMapPopup } from './popup/popup';
import { CardsList } from './list/list';
import styles from './map.module.css';

const MAP_ACCESS_TOKEN = process.env.REACT_APP_MAP_TOKEN as string;

type RestaurantsMapProps = {
  className?: string;
  data: Partial<StoreModel>[];
};

function getStoresMarkers(stores: Partial<StoreModel>[]) {
  return stores
    ?.map((store) => ({
      latitude: store.latitude as number,
      longitude: store.longitude as number,
      data: store,
      variant: store.isNeedAttention
        ? MarkerVariant.Alert
        : MarkerVariant.Default,
    }))
    .filter((marker) => marker.longitude && marker.latitude);
}

export const RestaurantsMap = ({ className, data }: RestaurantsMapProps) => {
  const [markers, setMarkers] = useState(getStoresMarkers(data));
  const [selectedStore, setSelectedStore] = useState<Partial<StoreModel>>();
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setMarkers(getStoresMarkers(data));
  }, [data]);

  const getPopupContent = (markerData: Partial<StoreModel>) => {
    return <RestaurantsMapPopup store={markerData} />;
  };

  const selectedStoreMarker = useMemo(
    () => markers?.find((marker) => marker.data?.id === selectedStore?.id),
    [selectedStore, markers],
  );

  const highlightStoreMarker = (storeId?: string | null) => {
    setMarkers(() => {
      const markers = getStoresMarkers(data);
      return markers.map((marker) => {
        if (marker.data.id === storeId) {
          return {
            ...marker,
            variant: MarkerVariant.Active,
          };
        }
        return marker;
      });
    });
  };

  const onCardItemSelect = (store: Partial<StoreModel>) => {
    setSelectedStore(store);
    highlightStoreMarker(store.id);
  };

  return (
    <div className={clsx(className, styles.root, 'flex')}>
      <CardsList
        stores={data}
        onItemSelect={onCardItemSelect}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
      />
      <Map
        popupClassName={styles.popup}
        accessToken={MAP_ACCESS_TOKEN}
        markers={markers}
        fitBound={true}
        getPopupContent={getPopupContent}
        selectedMarker={selectedStoreMarker}
        onPopupClose={() => setSelectedStore(undefined)}
        style={{ width: '100%', height: 'auto' }}
      />
    </div>
  );
};
